import React from 'react';

import cs from 'classnames';
import get from 'lodash/get';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import StateButton from 'app/general/widgets/StateButton';
import {useUpdateTaskVersion} from 'app/tasks/api';
import {gt} from 'app/utils/i18n';

import {DocumentAlertBox} from './DocumentAlertBox';

import type {TaskInstanceSerializedType} from 'sweet-types';

interface Props {
  taskId: number;
  versionDeleted: boolean;
  taskCompleted: boolean;
}

function TaskUpgradeAvailableAlertBox({
  taskId,
  versionDeleted,
  taskCompleted,
}: Props): JSX.Element {
  const [updateTaskVersion] = useUpdateTaskVersion();

  let upgradeLink = (
    <StateButton
      className={cs(
        'ml-0 ml-sm-2 mt-2 mt-sm-0 btn-info btn btn-sm text-nowrap',
        {
          disabled: taskCompleted,
        }
      )}
      onClick={() =>
        taskCompleted ? undefined : updateTaskVersion(taskId).unwrap()
      }
      busyText={gt('Update this Task')}
    >
      {gt('Update this Task')}
    </StateButton>
  );

  if (taskCompleted) {
    upgradeLink = (
      <LazyTooltip
        tooltip={gt(
          'Mark this task as not completed before upgrading its version'
        )}
      >
        {upgradeLink}
      </LazyTooltip>
    );
  }

  const text: {
    type: 'info' | 'warning';
    title: string;
    content: string;
  } = versionDeleted
    ? {
        type: 'warning',
        title: gt('Version Deleted'),
        content: gt(
          'The version associated with this task was deleted, ' +
            'you may update the task to the current version to continue' +
            ' %(upgradeLink)s',
          {
            upgradeLink,
          }
        ),
      }
    : {
        type: 'info',
        title: gt('A Newer Version is Available'),
        content: gt('This task has a newer version available %(upgradeLink)s', {
          upgradeLink,
        }),
      };
  return (
    <DocumentAlertBox
      type={text.type}
      icon="fa-arrow-square-up"
      title={text.title}
      content={
        <div className="d-block d-sm-flex align-items-center justify-content-between">
          {text.content}
        </div>
      }
    />
  );
}

const TaskUnavailableDialog = () => {
  return (
    <DocumentAlertBox
      type={'warning'}
      icon="fa-times"
      title={gt('This task is no longer available')}
      content={
        <div className="d-block d-sm-flex align-items-center justify-content-between">
          {gt(
            'This task has been canceled or your access to it has been revoked. Any changes here will' +
              ' not be saved.'
          )}
        </div>
      }
    />
  );
};

export const showTaskUpgradeAvailableAlertBoxIfAvailable = (
  task: TaskInstanceSerializedType,
  taskDeleted: boolean
): JSX.Element | null => {
  const versionDeleted = task.assigned_version_content == null;
  const newVersionAvailable =
    get(task.template, 'assigned_object.current_version.id') !==
    get(task, 'assigned_version_content.id');

  return versionDeleted || newVersionAvailable ? (
    <TaskUpgradeAvailableAlertBox
      taskId={task.id!}
      versionDeleted={versionDeleted}
      taskCompleted={task.completed != null}
    />
  ) : taskDeleted ? (
    <TaskUnavailableDialog />
  ) : null;
};
